import { PushPin, Star, StarOutlined } from '@mui/icons-material';
import { Checkbox, type CheckboxProps } from '@mui/material';
import { StyleObj } from '../../@types';
import { FavoriteIcon, useFavoriteIconConfig } from '../../config/skinRegistry/configs';
import { mergeSx } from '../../utils/styles';

const makeStyles = (variant: string, uncheckedColor: string, checkedColor: string): StyleObj => {
  return {
    container: { py: 0, px: 2, '& svg': { fontSize: variant } },
    uncheckedIcon: {
      color: uncheckedColor,
    },
    checkedIcon: {
      color: checkedColor,
    },
  };
};

export type FavoritesCheckboxProps = Omit<CheckboxProps, 'checked'> & {
  isChecked: boolean;
  uncheckedColor?: string;
  checkedColor?: string;
  variant?: 'small' | 'medium' | 'large' | 'xl';
  iconType?: FavoriteIcon;
};

const iconMap: Record<FavoriteIcon, { unchecked: typeof Star; checked: typeof Star }> = {
  star: {
    unchecked: StarOutlined,
    checked: Star,
  },
  pin: {
    unchecked: PushPin,
    checked: PushPin,
  },
};

const FavoritesCheckbox = ({
  isChecked,
  uncheckedColor = 'neutral.400',
  checkedColor: checkedColorProp,
  variant = 'xl',
  iconType: iconTypeProp,
  sx,
  ...rest
}: FavoritesCheckboxProps) => {
  const { icon: iconTypeFromConfig, checkedColor: checkedColorFromConfig } = useFavoriteIconConfig();
  const iconType = iconTypeProp ?? iconTypeFromConfig;
  const checkedColor = checkedColorProp ?? checkedColorFromConfig ?? 'secondary.main';
  const styles = makeStyles(variant, uncheckedColor, checkedColor);
  const IconUnchecked = iconMap[iconType].unchecked;
  const IconChecked = iconMap[iconType].checked;

  return (
    <Checkbox
      icon={<IconUnchecked sx={styles.uncheckedIcon} />}
      checkedIcon={<IconChecked sx={styles.checkedIcon} />}
      checked={isChecked}
      disableRipple
      sx={mergeSx(styles.container, sx)}
      {...rest}
    />
  );
};

export default FavoritesCheckbox;
